import { Box, Select, Typography, styled, Chip, MenuItem } from "@mui/material";
import "../../theme/muiTypes.d.ts";

export type SelectSize = "default" | "small";

const selectSizeStyles: Record<
  SelectSize,
  { height: string; padding: string }
> = {
  default: { height: "48px", padding: "12px 32px 13px 16px" },
  small: { height: "40px", padding: "8px 32px 9px 16px" },
};

export const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== "error" && prop !== "selectSize",
})<{ error?: boolean; selectSize?: SelectSize }>(({
  theme,
  error,
  selectSize = "default",
}) => {
  const { height, padding } = selectSizeStyles[selectSize];

  return {
    borderRadius: "8px",
    backgroundColor: theme.palette.background?.secondary,
    display: "flex",
    height,
    width: "100%",
    boxSizing: "border-box",
    "& .MuiSelect-icon": {
      height: "16px",
      width: "16px",
    },
    "&.Mui-disabled": {
      opacity: "0.5",
      "& .MuiSelect-select": {},
    },
    "& .MuiSelect-select": {
      padding,
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    "&.MuiOutlinedInput-root": {
      "& fieldset": {
        border: `1px solid ${
          error ? theme.palette.status?.error : theme.palette.border?.medium
        }`,
      },
      "&.Mui-focused fieldset": {
        border: `1px solid ${
          error ? theme.palette.status?.error : theme.palette.primary?.main
        }`,
        outline: "none",
      },
      "&:hover:not(.Mui-disabled) fieldset": {
        border: `1px solid ${
          error ? theme.palette.status?.error : theme.palette.primary?.main
        }`,
        outline: "none",
      },
      "&.Mui-error fieldset": {
        border: `1px solid ${theme.palette.status?.error}`,
      },
    },
  };
});

export const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.text?.secondary,
  fontFamily: "Nunito, sans-serif",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  marginBottom: "2px",
  paddingLeft: "8px",
  display: "block",
}));

export const IconContainer = styled(Box)(() => ({
  pointerEvents: "none",
  position: "absolute",
  right: "15px",
  display: "flex",
  alignItems: "center",
}));

export const ValueContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: "100%",
  "& p": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text?.primary,
    fontSize: "16px",
    fontFamily: "Nunito, sans-serif",
    flex: 1,
  },
}));

export const ChipsContainer = styled(Box)({
  display: "flex",
  flexWrap: "nowrap",
  gap: "4px",
  flex: 1,
  overflowX: "auto",
  overflowY: "hidden",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  scrollbarWidth: "none",
});

export const StyledChip = styled(Chip)(({ theme }) => ({
  height: "24px",
  flexShrink: 0,
  backgroundColor: theme.palette.background?.default,
  border: `1px solid ${theme.palette.border?.light}`,
  "& .MuiChip-label": {
    fontSize: "14px",
    padding: "0 8px",
  },
  "& .MuiChip-deleteIcon": {
    margin: "0 4px 0 -4px",
  },
}));

export const SearchContainer = styled(Box)(({ theme }) => ({
  padding: "8px 16px",
  borderBottom: `1px solid ${theme.palette.border?.light}`,
  position: "sticky",
  top: 0,
  backgroundColor: "#fff",
  zIndex: 1,
}));

export const SelectionPanelContainer = styled(Box)({
  width: "100%",
  padding: 0,
});

export const TOOTH_SELECTION_DROPDOWN_WIDTH = 530;
export const SURFACE_SELECTION_DROPDOWN_WIDTH = 420;

const createSelectionDropdownPaperStyles = (width: number) => ({
  marginTop: "12px",
  padding: "10px 0 20px",
  borderRadius: "12px",
  border: "1px solid #B9CED5",
  background: "#FFF",
  boxShadow: "0 8px 14px 0 rgba(47, 57, 103, 0.10)",
  width,
  minWidth: width,
  maxWidth: width,
  boxSizing: "border-box",
  maxHeight: 480,
  "& .MuiList-root": {
    padding: 0,
    display: "flex",
    flexDirection: "column",
  },
});

export const toothSelectionDropdownPaperStyles = createSelectionDropdownPaperStyles(
  TOOTH_SELECTION_DROPDOWN_WIDTH,
);

export const surfaceSelectionDropdownPaperStyles = createSelectionDropdownPaperStyles(
  SURFACE_SELECTION_DROPDOWN_WIDTH,
);

export const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected?: boolean }>(({ isSelected }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "6px 12px",
  borderRadius: isSelected ? "4px" : undefined,
  backgroundColor: isSelected ? "#F5F9FD!important" : undefined,
  minHeight: "auto",
}));

export const SecondaryLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text?.secondary,
  fontSize: "14px",
  marginLeft: "75px",
}));

export const menuPaperStyles = {
  marginTop: "12px",
  padding: "10px",
  borderRadius: "12px",
  border: "1px solid #B9CED5",
  background: "#FFF",
  boxShadow: "0 8px 14px 0 rgba(47, 57, 103, 0.10)",
  maxHeight: 400,
  maxWidth: 800,
  "& .MuiList-root": {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
};
